import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Truck, Shield, Sparkles } from "lucide-react";
import { api, CATEGORIES } from "../lib/shop";
import { useT, localizedProduct } from "../lib/i18n";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

export default function Home() {
  const { t, lang } = useT();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: "ANAIZA SHOP",
    description: "Cuțite profesionale din oțel japonez HRC 60 și truse de scule premium crom-vanadium.",
    url: typeof window !== "undefined" ? window.location.origin : "",
    areaServed: "RO",
    paymentAccepted: ["Cash on delivery", "Credit Card"],
  };

  const banners = [
    {
      slug: "cutite",
      eyebrow: t("home.cutite.eye"),
      title: t("home.cutite.title"),
      subtitle: t("home.cutite.sub"),
      tagline: t("home.cutite.tag"),
      cta: t("home.cta.cutite"),
      image: CATEGORIES.find((c) => c.slug === "cutite").image,
    },
    {
      slug: "scule",
      eyebrow: t("home.scule.eye"),
      title: t("home.scule.title"),
      subtitle: t("home.scule.sub"),
      tagline: t("home.scule.tag"),
      cta: t("home.cta.scule"),
      image: CATEGORIES.find((c) => c.slug === "scule").image,
    },
  ];

  return (
    <div data-testid="home-page">
      <SEO
        title="Cuțite Japoneze HRC 60 & Truse Scule Premium"
        description="ANAIZA SHOP — Magazin online cu cuțite profesionale din oțel japonez HRC 60 și truse de scule crom-vanadium."
        image={`${typeof window !== "undefined" ? window.location.origin : ""}/og-image.jpg?v=2`}
        path="/"
        jsonLd={jsonLd}
      />

      <section className="relative bg-silver-shimmer" data-testid="split-landing">
        <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-10 md:pt-14 pb-6 md:pb-10">
          <div className="flex items-center justify-between text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gray-500 mb-6 md:mb-10">
            <span className="font-bold text-gray-900">{t("home.eyebrow")}</span>
            <span className="flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> {t("home.choose")}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
            {banners.map((b, i) => (
              <Link
                key={b.slug}
                to={`/categorie/${b.slug}`}
                className="group relative aspect-[3/4] md:aspect-[4/5] overflow-hidden bg-white border border-gray-200 hover:border-gray-900 transition-colors anim-fade-up"
                style={{ animationDelay: `${i * 120}ms` }}
                data-testid={`landing-banner-${b.slug}`}
              >
                <img
                  src={b.image}
                  alt={`${b.title} ${b.subtitle}`}
                  className="absolute inset-0 w-full h-full object-cover product-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/75 via-gray-900/15 to-transparent group-hover:from-gray-900/85 transition-all duration-500" />

                <span className="absolute top-3 left-3 md:top-4 md:left-4 bg-white/95 backdrop-blur-sm text-gray-900 text-[8px] sm:text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 md:px-3 md:py-1.5">
                  {b.eyebrow}
                </span>

                <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 md:p-8 text-white">
                  <h2 className="font-display text-2xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-[0.95]">
                    {b.title}
                    <br />
                    <span className="text-stroke-white">{b.subtitle}</span>
                  </h2>
                  <span className="mt-3 md:mt-5 inline-flex items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-white/90">
                    <span className="inline-block w-5 sm:w-8 h-px bg-white group-hover:w-10 sm:group-hover:w-16 transition-all duration-500" />
                    {b.cta}
                    <ArrowRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-4 md:pt-6 pb-20 md:pb-28">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400">{t("home.catalog.eye")}</p>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tighter mt-3">{t("home.catalog.title")}</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="featured-grid">
          {products.map((p, i) => <ProductCard key={p.id} p={localizedProduct(p, lang)} index={i} />)}
        </div>
      </section>

      <section className="bg-gray-50 border-t border-b border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { Icon: Truck,    t: t("trust.delivery"), d: t("trust.delivery.d") },
            { Icon: Shield,   t: t("trust.warranty"), d: t("trust.warranty.d") },
            { Icon: Award,    t: t("trust.brand"),    d: t("trust.brand.d") },
            { Icon: Sparkles, t: t("trust.payment"),  d: t("trust.payment.d") },
          ].map(({ Icon, t: tt, d }, i) => (
            <div key={i} className="flex gap-4 items-start">
              <Icon size={28} strokeWidth={1.5} className="text-gray-900 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-sm text-gray-900">{tt}</p>
                <p className="text-xs text-gray-500 mt-1">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
